"use client"

import type React from "react"
import { useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useConnectorSelection } from "../../../hooks/useConnectorSelection"
import { ROUTES } from "../../../constants/sync-flow"

// Components
import LoadingScreen from "../shared/LoadingScreen"
import StepIndicator from "../shared/StepIndicator"

import "./ConnectorSelection.css"

const ConnectorSelectionConnectScreen: React.FC = () => {
  const navigate = useNavigate()
  const { selectedConnector, connectors, loading, selectConnector, proceedToNext } = useConnectorSelection()

  const handleCancel = useCallback(() => {
    navigate(ROUTES.DASHBOARD)
  }, [navigate])

  const handleNext = useCallback(() => {
    if (proceedToNext()) {
      navigate(ROUTES.CONNECT_ACCOUNTS)
    }
  }, [proceedToNext, navigate])

  if (loading) {
    return (
      <LoadingScreen
        title="Loading Available Connectors"
        description="Please wait while we prepare your integration options..."
      />
    )
  }

  return (
    <div className="connector-selection">
      <div className="connector-selection-header">
        <h1>Welcome to NetzSync!</h1>
        <p>To start syncing your data, please connect to your platforms.</p>
        <StepIndicator currentStep={1} stepLabel="Select Connector" />
      </div>

      <div className="connector-options">
        {connectors.map((connector) => (
          <div
            key={connector.id}
            className={`connector-card ${selectedConnector === connector.id ? "selected" : ""} ${!connector.isAvailable ? "disabled" : ""}`}
            onClick={() => connector.isAvailable && selectConnector(connector.id)}
          >
            <div className="connector-checkbox">
              <input
                type="radio"
                name="connector"
                checked={selectedConnector === connector.id}
                onChange={() => selectConnector(connector.id)}
                disabled={!connector.isAvailable}
              />
            </div>

            <div className="connector-content">
              <div className="connector-logos">
                <div className="connector-logo">
                  <img src={connector.fromLogo || "/placeholder.svg"} alt={connector.fromPlatform} />
                  <span className="platform-name">{connector.fromPlatform}</span>
                </div>

                <div className="connector-arrow">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14"></path>
                    <path d="M12 5l7 7-7 7"></path>
                  </svg>
                </div>

                <div className="connector-logo">
                  <img src={connector.toLogo || "/placeholder.svg"} alt={connector.toPlatform} />
                  <span className="platform-name">{connector.toPlatform}</span>
                </div>
              </div>

              <div className="connector-details">
                <h3>{connector.name}</h3>
                <p>{connector.description}</p>
                {!connector.isAvailable && <div className="unavailable-badge">Coming Soon</div>}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="connector-actions">
        <button className="cancel-button" onClick={handleCancel}>
          Cancel
        </button>
        <button className="next-button" onClick={handleNext} disabled={!selectedConnector}>
          Next
        </button>
      </div>
    </div>
  )
}

export default ConnectorSelectionConnectScreen
